<script lang="ts">
    import Chart from 'chart.js/auto';
    import { Block, Input, Range } from 'framework7-svelte';
    import Graph from './Dashboard/Graph.svelte';
    import {
        SELECTABLE_MEASUREMENT_METRIC_FIELDS, 
        MEASUREMENT_FIELD_DELAY_DURATION
    } from "../constants/measurement-table-constants";
    import type {RouteMeasurements} from "../models/route-measurements.model";
    import {routeMeasurementsMappedStore} from "../stores/route-measurements-mapped.store";
    import {sectionMeasurementsDateTimesStore} from "../stores/section-measurement-date-times.store";
    import {getMeasurementFieldLabel} from "../i18n/translate";
    
    export let activeMeasurementProperty: string = MEASUREMENT_FIELD_DELAY_DURATION;
    export let showMeasurementPropertySelector: boolean = true;
    export let measurementsForGraph: RouteMeasurements[] | undefined = undefined;
    export let measurementTimeStamps: Date[] | undefined = undefined;
    export let showRouteFilter: boolean = true;

    $: graphMeasurements = measurementsForGraph ?? $routeMeasurementsMappedStore;
    $: graphTimestamps = measurementTimeStamps ?? $sectionMeasurementsDateTimesStore;
    
    let chart: Chart;
    let logZoomLevel: number = 0;
    const MAX_LOG_ZOOM = 10;


    $: measurementsByRouteIdSortedByTimestampAsc = mapMeasurementsToGraphData(graphMeasurements);

    function resetZoom() {
        logZoomLevel = 0;

        if (!chart) return;

        chart.resetZoom?.();
    }

    function handleZoomChange(newValue: number) {
        if (!chart) return;

        logZoomLevel = Math.min(Math.max(newValue, 0), MAX_LOG_ZOOM);

        const newZoomLevel = Math.exp(logZoomLevel);
        const currentZoomLevel = getCurrentZoomLevel(chart);

        if (Math.abs(newZoomLevel / currentZoomLevel - 1) <= Number.EPSILON) return;

        updateZoomBounds(newZoomLevel);
    }
    
    function mapMeasurementsToGraphData(measurements: RouteMeasurements[]): { [routeId: string]: RouteMeasurements[] } {
        let result: { [routeId: string]: RouteMeasurements[] } = {};

        measurements.forEach(measurement => {
            if (!result[measurement.routeId]) {
                result[measurement.routeId] = [];
            }
            result[measurement.routeId].push(measurement);
        });

        Object.values(result).forEach(arr =>
            arr.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
        );

        resetZoom();
        return result;
    }

    function onZoomChange() {
        if (!chart) return;
        const zoomLevel = getCurrentZoomLevel(chart);
        if (zoomLevel === Infinity) {
            return;
        }
        logZoomLevel = Math.log(zoomLevel);
    }

    function updateZoomBounds(newZoomLevel: number) {
        if (!chart) return;
        const newRange = getZoomScaleBounds(chart, newZoomLevel);
        chart.zoomScale('x', newRange);
    }

    function getCurrentZoomLevel(chart): number {
        if (!chart) return;
        const xScale = chart.scales.x;
        const initialX = chart.getInitialScaleBounds()['x'];
        return (initialX.max - initialX.min) / (xScale.max - xScale.min);
    }

    function getZoomScaleBounds(chart, zoomLevel) {
        if (!chart) return;

        const initialX = chart.getInitialScaleBounds()['x'];
        const fullRange = initialX.max - initialX.min;
        const newRange = Math.max(fullRange / zoomLevel, 1);
        const center = (initialX.min + initialX.max) / 2;

        return {
            min: center - newRange / 2,
            max: center + newRange / 2
        };
    }
</script>

<Block>
    {#if showMeasurementPropertySelector}
        <Input label="Meetwaarde" type="select" bind:value={activeMeasurementProperty}>
            {#each SELECTABLE_MEASUREMENT_METRIC_FIELDS as propertyName}
                <option value={propertyName}>{getMeasurementFieldLabel(propertyName)}</option>
            {/each}
        </Input>
    {:else}
        <h3>
            {getMeasurementFieldLabel(activeMeasurementProperty)}
        </h3>
    {/if}
    <div class="label">Zoomfactor</div>
    <Range min={0} max={MAX_LOG_ZOOM} step={0.0001} bind:value={logZoomLevel} onRangeChanged={handleZoomChange}></Range>
</Block>

<Graph
    handleZoomChange={onZoomChange}
    {activeMeasurementProperty}
    measurementsByRouteIdSortedByTimestampAsc={measurementsByRouteIdSortedByTimestampAsc}
    measurementTimeStamps={graphTimestamps}
    {showRouteFilter}
    bind:chart
/>

<style lang="scss">
    .label {
        margin-top: 16px;
        //margin-bottom: 16px;
    }
</style>
